import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Button,
  CustomFilter,
  EllipsisSpan,
  Form,
  Input,
  Key,
  Paging,
  PopConfirm,
  Table,
  useBreadcrumbRoutes,
  useSearchParams,
  Wrapper,
} from '@maxtropy/components';
import dayjs from 'dayjs';
import { Cascader, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { QUERY_FIELD, TABLE_FIELD } from '@/pages/AssetInformationManagement/constant';
import style from './index.module.scss';
import { useDeviceTypeCascaderOptions } from '@/pages/AssetInformationManagement/hooks';
import { apiV2AssetTemplateDelPost, apiV2AssetTemplatePagePost } from '@maxtropy/device-customer-apis-v2';
import { getCascaderLabelById } from '@/pages/AssetInformationManagement/utils';
import { isArray } from 'lodash-es';

const getColumns = (cascaderOptions: any[]) => [
  {
    title: '模版名称',
    dataIndex: TABLE_FIELD.TEMPLATE_NAME,
    width: 160,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备类型',
    dataIndex: TABLE_FIELD.DEVICE_TYPE_ID,
    width: 160,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={getCascaderLabelById(cascaderOptions, v)} />,
  },
  {
    title: '最后操作时间',
    dataIndex: TABLE_FIELD.UPDATE_TIME,
    width: 160,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={dayjs(v).format('YYYY-MM-DD HH:mm:ss')} />,
  },
  {
    title: '最后操作人',
    dataIndex: TABLE_FIELD.UPDATE_USERNAME,
    width: 160,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

const AssetInfoManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const breadcrumbRoutes = useBreadcrumbRoutes();
  // 请求竞态
  const requestCounter = useRef<number>(0);

  // todo 等接口,修改ts类型
  const pagingInfo = useSearchParams<any>(20);
  const { searchParams, setTotalCount, reset } = pagingInfo;

  const cascaderOptions = useDeviceTypeCascaderOptions();

  const fetch = useCallback(async () => {
    setLoading(true);
    const typeIds = form.getFieldValue(QUERY_FIELD.DEVICE_TYPE);
    // 后端只要叶子结点的id
    let collectTypeIds;
    if (typeIds && typeIds.length > 0) {
      collectTypeIds = isArray(typeIds[0]) ? typeIds.map((i: []) => i.at(-1)) : [typeIds.at(-1)];
    }

    const params = {
      page: searchParams.pageOffset,
      size: searchParams.pageSize,
      [QUERY_FIELD.TEMPLATE_NAME]: form.getFieldValue(QUERY_FIELD.TEMPLATE_NAME),
      [QUERY_FIELD.DEVICE_TYPE]: collectTypeIds ?? [],
    };

    const count = ++requestCounter.current;
    try {
      const resp = await apiV2AssetTemplatePagePost(params);
      if (count !== requestCounter.current) return;
      const { list, total } = resp;
      setData(list || []);
      setTotalCount(total || 0);
    } finally {
      if (count === requestCounter.current) {
        setLoading(false);
      }
    }
  }, [searchParams, form, setTotalCount]);

  useEffect(() => {
    fetch();
  }, [searchParams]);

  const lastColumns = [
    ...getColumns(cascaderOptions),
    {
      title: '操作',
      dataIndex: 'operation',
      width: 100,
      fixed: 'right' as 'right',
      render: (_: any, record: any) => {
        return (
          <Space size={16}>
            <Button
              type="link"
              onClick={() => {
                window.open(`/asset/equipment/assetInfo/view/${record.id}`, '_blank');
              }}
            >
              查看
            </Button>

            <Button
              type="link"
              onClick={() => {
                window.open(`/asset/equipment/assetInfo/edit/${record.id}`, '_blank');
              }}
            >
              编辑
            </Button>

            <PopConfirm
              placement="top"
              title="确认删除？删除后不可恢复，你还要继续吗？"
              onConfirm={() => onDelete(record?.id)}
            >
              <Button type="link">删除</Button>
            </PopConfirm>
          </Space>
        );
      },
    },
  ];

  // 设备类型搜索函数
  const deviceTypeFilter = (inputValue: string, path: any[]) =>
    path.some(option => option.label.toLowerCase().includes(inputValue.toLowerCase()));

  const onReset = () => {
    reset();
    pagingInfo.setPageOffset(1);
  };

  const onDelete = async (id: Key) => {
    await apiV2AssetTemplateDelPost({ id });
    fetch();
  };

  const search = () => {
    pagingInfo.setPageOffset(1);
  };

  const filters = (
    <CustomFilter
      form={form}
      onFinish={search}
      onReset={onReset}
      collapseOpen={false}
      initialValues={{
        [QUERY_FIELD.TEMPLATE_NAME]: undefined,
        [QUERY_FIELD.DEVICE_TYPE]: undefined,
      }}
    >
      <Form.Item label="模版名称" name={QUERY_FIELD.TEMPLATE_NAME}>
        <Input placeholder="请输入模版名称" maxLength={50} />
      </Form.Item>
      <Form.Item label="设备类型" name={QUERY_FIELD.DEVICE_TYPE}>
        <Cascader
          options={cascaderOptions}
          allowClear
          multiple
          maxTagCount="responsive"
          showCheckedStrategy="SHOW_CHILD"
          showSearch={{ filter: deviceTypeFilter }}
          placeholder={'请选择设备类型'}
          onChange={v => {
            form.setFieldValue(QUERY_FIELD.DEVICE_TYPE, v);
          }}
        />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <Wrapper filters={filters} routes={[...(breadcrumbRoutes?.routes ?? [])]} className={style.container}>
      <Space size={8} style={{ marginBottom: 10 }}>
        <Button
          type="primary"
          icon={<PlusOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />}
          onClick={() => {
            window.open(`/asset/equipment/assetInfo/create`, '_blank');
          }}
        >
          新建模版
        </Button>
      </Space>

      <Table columns={lastColumns} scroll={{ x: 1300 }} dataSource={data} rowKey="id" loading={loading} />
      <Paging pagingInfo={pagingInfo} />
    </Wrapper>
  );
};

export default AssetInfoManagement;
